import { Node, mergeAttributes, type Command } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { sanitizeMermaidCode, validateAndFixMermaid, looksLikeMermaid } from './mermaid-sanitizer';

// Render state types for better state management
type RenderState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success'; svg: string }
    | { status: 'error'; message: string; originalCode: string; sanitizedCode: string; fixesApplied: string[] };

// Tiptap Mermaid Node View Component with resize functionality
function MermaidNodeView({ node, updateAttributes }: any) {
    const [renderState, setRenderState] = useState<RenderState>({ status: 'idle' });
    const [isResizing, setIsResizing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showRawCode, setShowRawCode] = useState(false);

    // Initialize dimensions from node attributes (use saved values if they exist)
    const initialWidth = node.attrs.width || 700;
    const initialHeight = node.attrs.height || 525;

    const [dimensions, setDimensions] = useState({
        width: initialWidth,
        height: initialHeight,
    });
    const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
    const [aspectRatio, setAspectRatio] = useState(initialWidth / initialHeight);
    const containerRef = useRef<HTMLDivElement>(null);
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, shiftKey: false });
    const animationFrameRef = useRef<number | null>(null);
    const latestDimensionsRef = useRef({ width: initialWidth, height: initialHeight });
    const renderAttemptRef = useRef(0);

    // Initialize mermaid once
    useEffect(() => {
        mermaid.initialize({
            startOnLoad: false,
            securityLevel: 'loose',
            theme: 'default',
            logLevel: 'error', // Reduce console noise
        });
    }, []);

    // Render mermaid diagram with validation and auto-fix
    const renderDiagram = useCallback(async (code: string) => {
        if (!code || !code.trim()) {
            setRenderState({ status: 'idle' });
            return;
        }

        // Quick sanity check
        if (!looksLikeMermaid(code)) {
            setRenderState({
                status: 'error',
                message: 'Content does not appear to be a valid Mermaid diagram',
                originalCode: code,
                sanitizedCode: code,
                fixesApplied: [],
            });
            return;
        }

        setRenderState({ status: 'loading' });
        const currentAttempt = ++renderAttemptRef.current;

        try {
            // Step 1: Validate and auto-fix the code
            const validationResult = await validateAndFixMermaid(code);

            // Check if this render attempt is still current
            if (currentAttempt !== renderAttemptRef.current) {
                return; // A newer render was triggered, abandon this one
            }

            const codeToRender = validationResult.sanitizedCode || code;

            if (!validationResult.valid) {
                // Validation failed even after fixes - try rendering anyway as a last resort
                // Sometimes mermaid.parse is stricter than mermaid.render
                let renderError = validationResult.error || 'Invalid diagram syntax';
                try {
                    const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
                    const { svg } = await mermaid.render(id, codeToRender);

                    if (currentAttempt !== renderAttemptRef.current) return;

                    const processedSvg = processSvg(svg);
                    if (processedSvg) {
                        setRenderState({ status: 'success', svg: processedSvg.svg });
                        updateDimensionsFromSvg(processedSvg);

                        // If we fixed the code and it now works, save the fixed version
                        if (validationResult.fixesApplied && validationResult.fixesApplied.length > 0) {
                            updateAttributes({ code: codeToRender });
                        }
                        return;
                    }
                } catch (renderErr) {
                    // Render also failed - use the render error if it's more specific
                    const renderErrMsg = renderErr instanceof Error ? renderErr.message : String(renderErr);
                    console.error('[Mermaid] Fallback render also failed:', renderErrMsg);
                    if (renderErrMsg && renderError === 'Invalid diagram syntax') {
                        renderError = simplifyError(renderErrMsg);
                    }
                }

                setRenderState({
                    status: 'error',
                    message: renderError,
                    originalCode: code,
                    sanitizedCode: codeToRender,
                    fixesApplied: validationResult.fixesApplied || [],
                });
                return;
            }

            // Step 2: Render the validated/fixed code
            const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
            const { svg } = await mermaid.render(id, codeToRender);

            if (currentAttempt !== renderAttemptRef.current) return;

            const processedSvg = processSvg(svg);
            if (processedSvg) {
                setRenderState({ status: 'success', svg: processedSvg.svg });
                updateDimensionsFromSvg(processedSvg);

                // If fixes were applied, optionally save the fixed code
                if (validationResult.fixesApplied && validationResult.fixesApplied.length > 0 && validationResult.sanitizedCode !== code) {
                    console.log('[Mermaid] Auto-fixed code, saving changes:', validationResult.fixesApplied);
                    updateAttributes({ code: codeToRender });
                }
            } else {
                throw new Error('Failed to process SVG output');
            }
        } catch (error) {
            if (currentAttempt !== renderAttemptRef.current) return;

            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('[Mermaid] Render failed:', errorMessage);

            // Get sanitized code for display in error state
            const sanitizeResult = sanitizeMermaidCode(code);

            setRenderState({
                status: 'error',
                message: simplifyError(errorMessage),
                originalCode: code,
                sanitizedCode: sanitizeResult.code,
                fixesApplied: sanitizeResult.fixesApplied,
            });
        }
    }, [updateAttributes]);

    // Process SVG and extract dimensions
    const processSvg = (svg: string): { svg: string; width: number; height: number } | null => {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (!svgElement) return null;

        // Extract dimensions
        let width = parseFloat(svgElement.getAttribute('width') || '0');
        let height = parseFloat(svgElement.getAttribute('height') || '0');

        if (!width || !height || width < 200) {
            const viewBox = svgElement.getAttribute('viewBox');
            if (viewBox) {
                const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number);
                if (vbWidth && vbHeight) {
                    width = vbWidth;
                    height = vbHeight;
                }
            }
        }

        if (!width || !height) {
            width = 700;
            height = 525;
        }

        // Configure SVG for responsive display
        svgElement.setAttribute('width', '100%');
        svgElement.setAttribute('height', '100%');
        svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

        return { svg: svgElement.outerHTML, width, height };
    };

    // Update dimensions from processed SVG
    const updateDimensionsFromSvg = (processed: { width: number; height: number }) => {
        setOriginalDimensions({ width: processed.width, height: processed.height });
        setAspectRatio(processed.width / processed.height);

        const hasCustomDimensions = node.attrs.width !== 700 || node.attrs.height !== 525;
        if (!hasCustomDimensions) {
            setDimensions({ width: processed.width, height: processed.height });
            latestDimensionsRef.current = { width: processed.width, height: processed.height };
            updateAttributes({ width: processed.width, height: processed.height });
        } else {
            setDimensions({ width: node.attrs.width, height: node.attrs.height });
            latestDimensionsRef.current = { width: node.attrs.width, height: node.attrs.height };
        }
    };

    // Simplify error messages for display
    const simplifyError = (error: string): string => {
        return error
            .replace(/at line \d+, column \d+/gi, '')
            .replace(/line \d+:\d+/gi, '')
            .replace(/Syntax error in graph/i, 'Syntax error')
            .replace(/Lexical error on line \d+\./gi, 'Invalid characters')
            .substring(0, 150)
            .trim() || 'Invalid diagram syntax';
    };

    // Trigger render when code changes
    useEffect(() => {
        const code = node.attrs.code || '';
        renderDiagram(code);
    }, [node.attrs.code, renderDiagram]);

    // Cleanup animation frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Resize handlers with smooth animation and aspect ratio preservation
    const handleMouseDown = (e: React.MouseEvent, corner: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        resizeStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: dimensions.width,
            height: dimensions.height,
            shiftKey: e.shiftKey,
        };

        const handleMouseMove = (moveEvent: MouseEvent) => {
            moveEvent.preventDefault();
            moveEvent.stopPropagation();

            // Cancel previous animation frame if it exists
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            // Use requestAnimationFrame for smoother updates
            animationFrameRef.current = requestAnimationFrame(() => {
                const deltaX = moveEvent.clientX - resizeStartRef.current.x;
                const deltaY = moveEvent.clientY - resizeStartRef.current.y;

                let newWidth = resizeStartRef.current.width;
                let newHeight = resizeStartRef.current.height;

                // Determine if we should preserve aspect ratio
                // Preserve aspect ratio UNLESS shift key is held (shift = free resize)
                const preserveAspectRatio = !moveEvent.shiftKey;

                if (corner === 'se') {
                    // Southeast corner - resize both width and height
                    if (preserveAspectRatio) {
                        // Use the primary direction (larger absolute delta) to drive the resize
                        if (Math.abs(deltaX) > Math.abs(deltaY)) {
                            newWidth = Math.max(200, resizeStartRef.current.width + deltaX);
                            newHeight = Math.max(150, newWidth / aspectRatio);
                        } else {
                            newHeight = Math.max(150, resizeStartRef.current.height + deltaY);
                            newWidth = Math.max(200, newHeight * aspectRatio);
                        }
                    } else {
                        newWidth = Math.max(200, resizeStartRef.current.width + deltaX);
                        newHeight = Math.max(150, resizeStartRef.current.height + deltaY);
                    }
                } else if (corner === 'e') {
                    // East edge - resize width only
                    newWidth = Math.max(200, resizeStartRef.current.width + deltaX);
                    if (preserveAspectRatio) {
                        newHeight = Math.max(150, newWidth / aspectRatio);
                    }
                } else if (corner === 's') {
                    // South edge - resize height only
                    newHeight = Math.max(150, resizeStartRef.current.height + deltaY);
                    if (preserveAspectRatio) {
                        newWidth = Math.max(200, newHeight * aspectRatio);
                    }
                }

                const newDimensions = { width: newWidth, height: newHeight };
                latestDimensionsRef.current = newDimensions;
                setDimensions(newDimensions);
            });
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
            upEvent.preventDefault();
            upEvent.stopPropagation();

            console.log('[Mermaid] Mouse up - stopping resize');
            setIsResizing(false);

            // Cancel any pending animation frame
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            // Save dimensions to node attributes using the ref (which has the latest values)
            const finalWidth = Math.round(latestDimensionsRef.current.width);
            const finalHeight = Math.round(latestDimensionsRef.current.height);

            console.log('[Mermaid] Saving dimensions:', { width: finalWidth, height: finalHeight });

            updateAttributes({
                width: finalWidth,
                height: finalHeight,
            });

            // Verify the attributes were updated
            setTimeout(() => {
                console.log('[Mermaid] Attributes after update:', node.attrs);
            }, 50);

            // Remove event listeners
            document.removeEventListener('mousemove', handleMouseMove, true);
            document.removeEventListener('mouseup', handleMouseUp, true);
        };

        // Use capture phase to ensure we catch the events
        document.addEventListener('mousemove', handleMouseMove, true);
        document.addEventListener('mouseup', handleMouseUp, true);
    };

    const textAlign = node.attrs.textAlign || 'left';

    // Calculate margin based on alignment
    const getMarginStyle = () => {
        switch (textAlign) {
            case 'center':
                return '16px auto';
            case 'right':
                return '16px 0 16px auto';
            default:
                return '16px 0';
        }
    };

    // Copy code to clipboard
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Render error state with fallback UI
    if (renderState.status === 'error') {
        return (
            <NodeViewWrapper>
                <div
                    className="my-4 mermaid-container mermaid-error"
                    style={{
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        backgroundColor: '#fef2f2',
                        overflow: 'hidden',
                        maxWidth: '100%',
                    }}
                >
                    {/* Error Header */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            backgroundColor: '#fee2e2',
                            borderBottom: '1px solid #fca5a5',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                                <path
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4m0 4h.01"
                                    stroke="#dc2626"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span style={{ color: '#991b1b', fontWeight: 500, fontSize: '14px' }}>
                                Diagram Error
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setShowRawCode(!showRawCode)}
                                style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    backgroundColor: showRawCode ? '#dc2626' : 'white',
                                    color: showRawCode ? 'white' : '#dc2626',
                                    border: '1px solid #dc2626',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                {showRawCode ? 'Hide Code' : 'Show Code'}
                            </button>
                            <button
                                onClick={() => copyToClipboard(renderState.originalCode)}
                                style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    backgroundColor: 'white',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                                title="Copy diagram code"
                            >
                                Copy
                            </button>
                            <button
                                onClick={() => renderDiagram(node.attrs.code)}
                                style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                                title="Retry rendering"
                            >
                                Retry
                            </button>
                        </div>
                    </div>

                    {/* Error Message */}
                    <div style={{ padding: '12px 16px' }}>
                        <p style={{ color: '#991b1b', fontSize: '13px', margin: 0 }}>
                            {renderState.message}
                        </p>

                        {/* Fixes Applied Info */}
                        {renderState.fixesApplied.length > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                <span style={{ fontWeight: 500 }}>Auto-fixes attempted: </span>
                                {renderState.fixesApplied.join(', ')}
                            </div>
                        )}
                    </div>

                    {/* Raw Code Display */}
                    {showRawCode && (
                        <div
                            style={{
                                margin: '0 16px 16px',
                                padding: '12px',
                                backgroundColor: '#1f2937',
                                borderRadius: '6px',
                                overflow: 'auto',
                                maxHeight: '300px',
                            }}
                        >
                            <pre
                                style={{
                                    margin: 0,
                                    fontSize: '12px',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    color: '#e5e7eb',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {renderState.sanitizedCode}
                            </pre>
                        </div>
                    )}
                </div>
            </NodeViewWrapper>
        );
    }

    // Render loading state
    if (renderState.status === 'loading' || renderState.status === 'idle') {
        return (
            <NodeViewWrapper>
                <div
                    className="my-4 mermaid-container"
                    style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '24px',
                        minHeight: '80px',
                        backgroundColor: '#f9fafb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                    }}
                >
                    {/* Simple loading spinner */}
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ animation: 'spin 1s linear infinite' }}
                    >
                        <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#d1d5db"
                            strokeWidth="3"
                            fill="none"
                        />
                        <path
                            d="M12 2a10 10 0 019.798 7.853"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeLinecap="round"
                            fill="none"
                        />
                    </svg>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        Rendering diagram...
                    </span>
                    <style>{`
                        @keyframes spin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            </NodeViewWrapper>
        );
    }

    // Success state - render the diagram
    return (
        <NodeViewWrapper>
            <div
                ref={containerRef}
                className="mermaid-container"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => !isResizing && setIsHovered(false)}
                style={{
                    position: 'relative',
                    width: `${dimensions.width}px`,
                    height: `${dimensions.height}px`,
                    margin: getMarginStyle(),
                    border: isResizing ? '2px solid #0066cc' : (isHovered ? '1px solid #e0e0e0' : '1px solid transparent'),
                    borderRadius: '4px',
                    overflow: 'hidden',
                    cursor: isResizing ? 'nwse-resize' : 'default',
                    transition: 'border-color 0.15s ease-in-out',
                    opacity: node.attrs.pendingReplaceEditId ? 0.4 : 1,
                    pointerEvents: node.attrs.pendingReplaceEditId ? 'none' : 'auto',
                    outline: node.attrs.pendingReplaceEditId ? '2px dashed #f59e0b' : 'none',
                }}
            >
                <div
                    className="mermaid-svg-container"
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    dangerouslySetInnerHTML={{ __html: renderState.status === 'success' ? renderState.svg : '' }}
                />

                {/* Resize Handles - only show on hover or when resizing */}
                {(isHovered || isResizing) && (
                    <>
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 'se')}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#0066cc',
                                cursor: 'nwse-resize',
                                borderRadius: '0 0 4px 0',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize"
                        />
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 'e')}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                right: 0,
                                width: '8px',
                                height: '40px',
                                backgroundColor: '#0066cc',
                                cursor: 'ew-resize',
                                transform: 'translateY(-50%)',
                                borderRadius: '0 4px 4px 0',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize width"
                        />
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 's')}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                width: '40px',
                                height: '8px',
                                backgroundColor: '#0066cc',
                                cursor: 'ns-resize',
                                transform: 'translateX(-50%)',
                                borderRadius: '0 0 4px 4px',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize height"
                        />
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
}

/**
 * Custom Tiptap extension for Mermaid diagrams
 *
 * Renders Mermaid code blocks as diagrams
 */
export const Mermaid = Node.create({
    name: 'mermaid',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            code: {
                default: '',
                // Parse from data-code attribute when loading HTML
                parseHTML: element => {
                    const dataCode = element.getAttribute('data-code');
                    if (!dataCode) return undefined;

                    // Check if the code is base64 encoded
                    const encoding = element.getAttribute('data-code-encoding');
                    if (encoding === 'base64') {
                        try {
                            return decodeURIComponent(escape(atob(dataCode)));
                        } catch {
                            console.error('[Mermaid] Failed to decode base64 in addAttributes parseHTML');
                            return dataCode;
                        }
                    }
                    return dataCode;
                },
            },
            width: {
                default: 700,
                parseHTML: element => {
                    const width = element.getAttribute('data-width');
                    return width ? parseInt(width, 10) : undefined;
                },
            },
            height: {
                default: 525,
                parseHTML: element => {
                    const height = element.getAttribute('data-height');
                    return height ? parseInt(height, 10) : undefined;
                },
            },
            textAlign: {
                default: 'left',
                parseHTML: element => element.getAttribute('data-align') || undefined,
            },
            nodeId: {
                default: null,
                parseHTML: element => element.getAttribute('data-node-id') || null,
            },
            pendingReplaceEditId: {
                default: null,
                parseHTML: element => element.getAttribute('data-pending-edit-id') || null,
            },
            pendingReplaceCode: {
                default: null,
                parseHTML: element => element.getAttribute('data-pending-code') || null,
            },
        };
    },

    parseHTML() {
        console.log('[Mermaid Extension] parseHTML() called - registering parsers');
        return [
            // CRITICAL: Use HIGH priority to ensure this parser is checked BEFORE StarterKit's codeBlock
            // This prevents codeBlock from consuming our mermaid code blocks
            {
                tag: 'pre',
                priority: 100, // High priority to override StarterKit's codeBlock
                getAttrs: (dom) => {
                    if (dom instanceof HTMLElement) {
                        const code = dom.querySelector('code.language-mermaid');
                        if (code) {
                            // Use textContent - it automatically decodes HTML entities AND strips all HTML tags
                            // This prevents nested <pre><code> tags from being included in the mermaid code
                            let mermaidCode = code.textContent || '';

                            console.log('[Mermaid Extension] Parsing pre>code.language-mermaid (HIGH PRIORITY)');
                            console.log('[Mermaid Extension] Raw extracted code:', mermaidCode);
                            console.log('[Mermaid Extension] Code length:', mermaidCode.length);

                            // Additional safety: manually decode any remaining HTML entities
                            // (textContent should handle this, but being extra safe)
                            mermaidCode = mermaidCode
                                .replace(/&amp;/g, '&')
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>')
                                .replace(/&quot;/g, '"')
                                .replace(/&#39;/g, "'");

                            console.log('[Mermaid Extension] After manual decode:', mermaidCode);
                            console.log('[Mermaid Extension] Contains --&gt;?', mermaidCode.includes('--&gt;'));
                            console.log('[Mermaid Extension] Contains &amp;?', mermaidCode.includes('&amp;'));

                            return {
                                code: mermaidCode.trim(),
                            };
                        }
                    }
                    return false; // Don't match if no mermaid code inside
                },
            },
            {
                tag: 'div[data-mermaid]',
                priority: 100, // High priority for div format too
                getAttrs: (dom) => {
                    console.log('[Mermaid Extension] getAttrs called for div[data-mermaid]', dom);
                    if (dom instanceof HTMLElement) {
                        let code = dom.getAttribute('data-code') || '';
                        const encoding = dom.getAttribute('data-code-encoding');
                        const width = dom.getAttribute('data-width');
                        const height = dom.getAttribute('data-height');
                        const textAlign = dom.getAttribute('data-align') || 'left';

                        // Decode base64-encoded code (new format)
                        if (code && encoding === 'base64') {
                            try {
                                code = decodeURIComponent(escape(atob(code)));
                                console.log('[Mermaid Extension] Decoded base64 mermaid code, length:', code.length);
                            } catch (e) {
                                console.error('[Mermaid Extension] Failed to decode base64 code, using raw value:', e);
                            }
                        } else if (code && !encoding) {
                            // Legacy unencoded format - newlines may have been stripped by HTML parser
                            // Check if the code looks like it lost its newlines
                            console.log('[Mermaid Extension] Legacy unencoded data-code detected');
                        }

                        // If no data-code attribute, try to extract from nested <code> element (old format)
                        if (!code) {
                            const codeElement = dom.querySelector('code.language-mermaid');
                            if (codeElement) {
                                code = codeElement.textContent || '';
                                console.log('[Mermaid Extension] Extracted code from nested <code> element (old format)');
                            }
                        }

                        if (!code) {
                            console.warn('[Mermaid Extension] No code found in div[data-mermaid]');
                            return false; // Don't match if no code
                        }

                        console.log('[Mermaid Extension] Parsing div[data-mermaid], code:', code.substring(0, 100));
                        console.log('[Mermaid Extension] Dimensions:', { width, height, textAlign });

                        return {
                            code,
                            width: width ? parseInt(width, 10) : 700,
                            height: height ? parseInt(height, 10) : 525,
                            textAlign,
                        };
                    }
                    return false;
                },
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const width = node.attrs.width || 700;
        const height = node.attrs.height || 525;
        const code = node.attrs.code || '';

        console.log('[Mermaid Extension] renderHTML called - outputting dimensions:', {
            width,
            height,
            code: code.substring(0, 50),
            align: node.attrs.textAlign
        });

        // CRITICAL: Base64 encode the mermaid code to prevent HTML attribute normalization
        // from stripping newlines (HTML parsers convert newlines in attributes to spaces)
        const encodedCode = code ? btoa(unescape(encodeURIComponent(code))) : '';

        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-mermaid': '', // Marker to identify this as a mermaid diagram
                'data-code': encodedCode,
                'data-code-encoding': 'base64', // Flag so parser knows to decode
                'data-width': String(width),
                'data-height': String(height),
                'data-align': node.attrs.textAlign || 'left',
                'data-node-id': node.attrs.nodeId ?? '',
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(MermaidNodeView);
    },

    addCommands() {
        return {
            setMermaid:
                (code: string): Command =>
                ({ commands }: any) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: { code },
                    });
                },
        };
    },

    addStorage() {
        return {
            markdown: {
                serialize(state: any, node: any) {
                    const id = node.attrs.nodeId ?? 'unknown';
                    state.write(
                        `<!-- diagram:${id} -->\n\`\`\`mermaid\n${node.attrs.code ?? ''}\n\`\`\``
                    );
                    state.ensureNewLine();
                    state.write('\n');
                },
            },
        };
    },
});

/**
 * Helper: Convert markdown code blocks with language="mermaid" to Mermaid nodes
 *
 * This should be called after markdown deserialization to convert
 * ```mermaid blocks into actual Mermaid diagram nodes
 */
export function convertMermaidCodeBlocks(content: any): any {
    if (Array.isArray(content)) {
        return content.map((node) => convertMermaidCodeBlocks(node));
    }

    if (typeof content === 'object' && content !== null) {
        // Check if this is a code block with language="mermaid"
        if (content.type === 'codeBlock' && content.attrs?.language === 'mermaid') {
            // Convert to mermaid node
            const code = content.content?.[0]?.text || '';
            return {
                type: 'mermaid',
                attrs: { code },
            };
        }

        // Recursively process children
        if (content.content) {
            return {
                ...content,
                content: convertMermaidCodeBlocks(content.content),
            };
        }
    }

    return content;
}
